import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Api } from "./api";
import type { Passkey } from "./lib/passkey";

export type Deps = {
  api: Api;
  passkey: Passkey;
  now: () => number;
  /** Poll interval for "waiting for your app" during setup. */
  pollMs: number;
  copy: (text: string) => Promise<void>;
  /** Full-page navigation for OAuth and checkout redirects. */
  redirect: (url: string) => void;
  /** The browser's IANA time zone (injected so tests are deterministic). Defaults to Intl. */
  timeZone?: () => string | undefined;
};

const Ctx = createContext<Deps | null>(null);

export function DepsProvider({ deps, children }: { deps: Deps; children: ReactNode }) {
  return <Ctx.Provider value={deps}>{children}</Ctx.Provider>;
}

export function useDeps(): Deps {
  const d = useContext(Ctx);
  if (!d) throw new Error("useDeps must be used inside <DepsProvider>");
  return d;
}

export type Resource<T> =
  | { status: "loading"; data: null; error: null; reload: () => void; set: (t: T) => void }
  | { status: "ok"; data: T; error: null; reload: () => void; set: (t: T) => void }
  | { status: "error"; data: null; error: Error; reload: () => void; set: (t: T) => void };

/** Load data from the api. Stale responses (after a newer load or unmount) are dropped. */
export function useResource<T>(load: (api: Api) => Promise<T>, key = ""): Resource<T> {
  const { api } = useDeps();
  const [state, setState] = useState<{
    status: "loading" | "ok" | "error";
    data: T | null;
    error: Error | null;
    key: string;
  }>({ status: "loading", data: null, error: null, key });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` and `tick` are the explicit reload triggers.
  useEffect(() => {
    let live = true;
    // A reload of the same key keeps showing the current data; a new key starts from "loading".
    setState((s) => (s.status === "ok" && s.key === key ? s : { status: "loading", data: null, error: null, key }));
    loadRef.current(api).then(
      (data) => live && setState({ status: "ok", data, error: null, key }),
      (error: unknown) =>
        live &&
        setState({
          status: "error",
          data: null,
          error: error instanceof Error ? error : new Error(String(error)),
          key,
        }),
    );
    return () => {
      live = false;
    };
  }, [api, key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const set = useCallback((data: T) => setState((s) => ({ status: "ok", data, error: null, key: s.key })), []);
  const { key: _key, ...rest } = state;
  return { ...rest, reload, set } as Resource<T>;
}
